import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessageSquare } from "lucide-react";

export function SendSmsDialog({ placeId, disabled }) {
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("custom");
  const [corps, setCorps] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) {
      api
        .get("/sms-templates")
        .then((res) => setTemplates(res.data.items))
        .catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    if (templateId === "custom") return;
    const t = templates.find((tp) => tp.id === templateId);
    if (t) setCorps(t.corps);
  }, [templateId, templates]);

  const handleSend = async () => {
    if (!corps.trim()) {
      toast.error("Le message ne peut pas etre vide");
      return;
    }
    setSending(true);
    try {
      const res = await api.post(`/prospects/${placeId}/send-sms`, {
        template_id: templateId === "custom" ? null : templateId,
        corps,
      });
      toast.success(`SMS envoye a ${res.data.to}`);
      setOpen(false);
      setCorps("");
      setTemplateId("custom");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'envoi du SMS");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) {
          setCorps("");
          setTemplateId("custom");
        }
      }}
    >
      <Button
        variant="outline"
        size="icon"
        className="rounded-none"
        disabled={disabled}
        data-testid={`send-sms-button-${placeId}`}
        onClick={() => setOpen(true)}
      >
        <MessageSquare size={14} />
      </Button>
      <DialogContent className="rounded-none" data-testid="send-sms-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-tight">Envoyer un SMS</DialogTitle>
          <DialogDescription className="font-mono text-xs">
            Choisissez un modele ou ecrivez un message personnalise
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Select value={templateId} onValueChange={setTemplateId}>
            <SelectTrigger data-testid="sms-template-select" className="rounded-none font-mono text-xs uppercase">
              <SelectValue placeholder="Modele" />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="custom" data-testid="sms-template-option-custom" className="font-mono text-xs uppercase">
                Message personnalise
              </SelectItem>
              {templates.map((t) => (
                <SelectItem key={t.id} value={t.id} data-testid={`sms-template-option-${t.id}`} className="font-mono text-xs uppercase">
                  {t.nom}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Textarea
            data-testid="sms-body-textarea"
            value={corps}
            onChange={(e) => setCorps(e.target.value)}
            rows={4}
            placeholder="Ex: Bonjour {nom}, ..."
            className="rounded-none font-mono text-sm"
          />
          <p className="font-mono text-xs text-muted-foreground">
            Variables disponibles: {"{nom}"} {"{metier}"} {"{nb_avis}"} {"{adresse}"} {"{telephone}"}
          </p>
        </div>
        <DialogFooter>
          <Button
            data-testid="send-sms-confirm-button"
            onClick={handleSend}
            disabled={sending}
            className="rounded-none font-mono uppercase tracking-wider"
          >
            {sending ? "Envoi..." : "Envoyer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
